import Image from 'next/image';
import type { CSSProperties, HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { useBlockedUsers } from '../../../graphdebate/src/hooks/useBlockedUsers';
import { useAvatarFallbackSrc } from '../hooks/useAvatarFallbackSrc';
import defaultAvatar from '../images/gd-default-avatar.png';
import type { Author, Profile } from '../types/api';
import { isAcceptableSizeToBeBlurredByNextImage } from '../utils/image';
import { sizesToClassName, sizesToPixels } from './Avatar-const';
import { DefaultLetterAvatar } from './DefaultLetterAvatar';

const defaultClassName = 'Avatar bg-white dark:bg-[var(--dk-border)] select-none object-cover';

export type AvatarSizes = keyof typeof sizesToClassName;

export type AvatarPropsType = {
  type?: Author['__typename'];
  pseudo?: string;
  profile?: Profile;
  picture: string | undefined | null;
  size?: AvatarSizes;
  className?: string;
  style?: CSSProperties;
  legacyImage?: boolean;
  noRound?: boolean;
  noCenter?: boolean;
  isUserBlocked?: boolean;
};

export const Avatar = forwardRef<
  HTMLImageElement,
  AvatarPropsType & Omit<HTMLAttributes<HTMLImageElement>, 'placeholder'>
>((props, ref) => {
  const {
    type,
    pseudo,
    profile,
    picture,
    size = 'medium',
    className,
    style,
    legacyImage,
    noRound,
    noCenter,
    isUserBlocked,
    ...others
  } = props;

  const { isBlocked, isNative } = useBlockedUsers();
  const userId = profile?.user?.id;
  const isBlockedUser = isUserBlocked || (isNative && isBlocked(userId));

  const imgProps = useAvatarFallbackSrc(profile?.picture || picture);

  const sizeClassName = sizesToClassName[size];
  const fullAvatarClassnames = twMerge(
    defaultClassName,
    !noCenter && 'self-center',
    !noRound && 'rounded-full',
    sizeClassName,
    className,
  );

  const commonAvatarProps = {
    ref,
    className: fullAvatarClassnames,
    style,
  };

  const commonImgAvatarProps = {
    ...commonAvatarProps,
    ...imgProps,
    ...others,
    alt: 'avatar',
  };

  const isUser = type === 'User' || profile;
  const hasCustomPicture = !imgProps.src.split('/').at(-1)?.includes('avatar');

  // Native blocking: force default avatar for blocked users
  if (isUser && (isBlockedUser || !hasCustomPicture)) {
    return (
      <DefaultLetterAvatar
        pseudo={pseudo}
        profile={profile || { pseudo: 'UnknownUser' }}
        size={size}
        isUserBlocked={isBlockedUser}
        {...commonAvatarProps}
      />
    );
  }

  if (legacyImage) {
    return <LegacyImgAvatar {...commonImgAvatarProps} draggable={false} />;
  }

  if (size === 'full') {
    throw new Error(
      'Unsupported full size for now. It needs to be implemented with the right Image properties. Cf. Next.js doc.',
    );
  }

  const { w, h } = sizesToPixels[size];
  const shouldHaveBlurEffectOnLoad = isAcceptableSizeToBeBlurredByNextImage(w, h);

  return (
    <Image
      {...commonImgAvatarProps}
      width={w}
      height={h}
      {...(shouldHaveBlurEffectOnLoad
        ? {
            placeholder: 'blur',
            blurDataURL: defaultAvatar.blurDataURL,
          }
        : {})}
    />
  );
});

Avatar.displayName = 'Avatar';

const LegacyImgAvatar = forwardRef<
  HTMLImageElement,
  Omit<AvatarPropsType, 'profile' | 'picture'> &
    Omit<HTMLAttributes<HTMLImageElement>, 'placeholder'>
>((props, ref) => {
  return <img ref={ref} {...props} />;
});
